/* global BX24 */

class BitrixResize {
  constructor() {
    this.isInitialized = false;
    this.resizeTimeout = null;
    this.lastHeight = 0;
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.isResizing = false;
    this.pendingResize = false;
    // null — состояние ещё не применялось: первый вызов setPageScrollable
    // должен выставить стили, а не решить, что менять нечего.
    this.isPageScrollable = null;
  }

  isBitrixAvailable() {
    return typeof BX24 !== "undefined" && BX24 !== null;
  }

  // Признак «страница живёт внутри iframe Битрикса и высотой окна управляем мы».
  // В этом режиме внутренний скролл документа выключен (overflow: hidden), и
  // прокручивать документ из кода нельзя: пользователь потом не вернёт его
  // руками — скроллит только внешняя страница Битрикса.
  isEmbedded() {
    return this.isInitialized && this.isBitrixAvailable();
  }

  init() {
    if (!this.isBitrixAvailable()) {
      console.log("BX24 API недоступен, автоматическое изменение размера отключено");
      return;
    }

    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    try {
      // Внутренний скролл страницы выключается, пока окно вмещает контент:
      // прокручивать должен Битрикс, а не документ внутри iframe. Но насовсем
      // выключать его нельзя — см. setPageScrollable.
      this.setPageScrollable(false);

      this.startResizeWatcher();
      this.scheduleResize(200);
      console.log("✅ Автоматическое изменение размера Bitrix окна включено");
    } catch (error) {
      console.error("❌ Ошибка при инициализации изменения размера:", error);
    }
  }

  startResizeWatcher() {
    const appContainer = document.getElementById("root");

    // ResizeObserver только на #root — не на body/documentElement,
    // иначе BX24.resizeWindow сам триггерит Observer и создаёт петлю
    if (typeof ResizeObserver !== "undefined" && appContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isResizing) {
          // Пересчёт идёт прямо сейчас: событие не выбрасываем, а помечаем —
          // иначе рост контента, пришедшийся на эти 500 мс, теряется навсегда
          // и iframe остаётся ниже страницы, а хвост недостижим.
          this.pendingResize = true;
          return;
        }
        this.scheduleResize();
      });
      this.resizeObserver.observe(appContainer);
    }

    // MutationObserver ТОЛЬКО на class атрибут body:
    // Bootstrap добавляет/убирает "modal-open" при открытии/закрытии любой модалки —
    // это единственный безопасный триггер без создания петли
    if (typeof MutationObserver !== "undefined") {
      this.mutationObserver = new MutationObserver((mutations) => {
        const hasModalClassChange = mutations.some(
          (m) => m.type === "attributes" && m.attributeName === "class"
        );
        if (!hasModalClassChange) {
          return;
        }
        if (this.isResizing) {
          this.pendingResize = true;
          return;
        }
        // Небольшая задержка чтобы модалка успела отрендериться
        this.scheduleResize(400);
      });
      this.mutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
        subtree: false,
        childList: false,
      });
    }
  }

  /**
   * Внутренний скролл документа.
   *
   * Пока окно Битрикса вмещает всю страницу, внутренний скролл лишний: он даёт
   * вторую полосу прокрутки рядом с полосой самого Битрикса. Но выключать его
   * безусловно нельзя. Пересчёт высоты не мгновенный — он идёт через сообщение
   * родителю и его перекладку, — и в эти доли секунды контент выше окна.
   * Раньше в такой момент хвост страницы оказывался обрезан и недостижим:
   * overflow: hidden запрещает прокрутку пользователю, колесо крутило внешнюю
   * страницу, то есть сам iframe, а не содержимое. Это и есть «прокрутка
   * встала, ни вверх, ни вниз».
   *
   * Поэтому скролл возвращается ровно на то время, пока контент не помещается.
   */
  setPageScrollable(scrollable) {
    if (this.isPageScrollable === scrollable) {
      return;
    }

    this.isPageScrollable = scrollable;
    const value = scrollable ? "" : "hidden";
    document.documentElement.style.overflow = value;
    document.body.style.overflow = value;
  }

  // Контент не помещается в текущее окно Битрикса. window.innerHeight внутри
  // iframe — это и есть его нынешняя высота.
  isContentClipped(contentHeight) {
    const frameHeight = window.innerHeight || 0;
    return frameHeight > 0 && contentHeight > frameHeight + 8;
  }

  /**
   * Прокрутка к элементу изнутри приложения.
   *
   * Страницу прокручивает Битрикс, поэтому просим об этом его: BX24 шлёт
   * родителю setScroll с позицией внутри окна приложения. Своего смещения в
   * странице Битрикса приложение не знает, так что другой трактовки у этого
   * числа быть не может.
   *
   * Перед прокруткой догоняем высоту: цель может быть за нижней границей окна,
   * и тогда родителю просто некуда вести. scrollIntoView вызывается тоже — он
   * сработает только если внутренний скролл сейчас доступен, то есть окно
   * отстало; запереть страницу он больше не может.
   */
  scrollToNode(node, options = { behavior: "smooth", block: "start" }) {
    if (!node || !this.isEmbedded()) {
      return false;
    }

    const run = () => {
      try {
        if (typeof node.scrollIntoView === "function") {
          node.scrollIntoView(options);
        }

        const rect = node.getBoundingClientRect();
        const pageTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const target = Math.max(0, Math.round(rect.top + pageTop) - 16);
        BX24.scrollParentWindow(target);
      } catch (error) {
        console.error("❌ Ошибка при прокрутке окна Bitrix:", error);
      }
    };

    this.forceResize();
    run();
    // Второй заход — после того как родитель переложил iframe под новую высоту.
    setTimeout(run, 400);
    return true;
  }

  // Высота открытых модалок (position: fixed — не входят в scrollHeight #root)
  getVisibleModalHeight() {
    const modals = Array.from(
      document.querySelectorAll(".modal.show:not(.bitrix-resize-skip)")
    );

    if (modals.length === 0) {
      return 0;
    }

    return modals.reduce((maxHeight, modal) => {
      const dialog = modal.querySelector(".modal-dialog");
      if (!dialog) return maxHeight;

      // scrollHeight даёт полную высоту даже если контент обрезан viewport'ом
      const dialogHeight =
        dialog.scrollHeight ||
        dialog.offsetHeight ||
        dialog.getBoundingClientRect().height;

      // 20px margin сверху + 20px снизу (из CSS .modal-dialog { margin: 20px auto })
      const totalHeight = Math.ceil(dialogHeight) + 40;
      return Math.max(maxHeight, totalHeight);
    }, 0);
  }

  getContentHeight() {
    const appContainer = document.getElementById("root");

    const pageHeight = Math.max(
      appContainer?.scrollHeight || 0,
      appContainer?.offsetHeight || 0
    );

    const modalHeight = this.getVisibleModalHeight();

    return Math.max(pageHeight, modalHeight);
  }

  scheduleResize(delay = 300) {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateWindowSize();
    }, delay);
  }

  updateWindowSize() {
    if (!this.isBitrixAvailable() || this.isResizing) {
      return;
    }

    try {
      const contentHeight = this.getContentHeight();
      const minHeight = 700;
      const safeHeight = Math.max(minHeight, Math.ceil(contentHeight + 24));

      // Состояние скролла сверяется всегда, даже когда сам размер менять не
      // будем: иначе страница осталась бы запертой из-за порога ниже.
      this.setPageScrollable(this.isContentClipped(contentHeight));

      // Рост показываем сразу — иначе низ страницы обрезан. Уменьшение тянем
      // лениво: при правке количеств высота скачет на десятки пикселей, а
      // каждый пересчёт — это перекладка всей страницы Битрикса, то есть рывки.
      const delta = safeHeight - this.lastHeight;
      if (delta >= 0 ? delta < 30 : delta > -120) {
        return;
      }

      // Блокируем повторный вызов пока iframe не устаканится
      this.isResizing = true;
      this.lastHeight = safeHeight;
      BX24.resizeWindow(window.innerWidth, safeHeight);
      console.log(`📏 Размер Bitrix iframe обновлен: ${safeHeight}px`);

      setTimeout(() => {
        this.isResizing = false;
        if (this.pendingResize) {
          this.pendingResize = false;
          this.scheduleResize(0);
        }
      }, 500);
    } catch (error) {
      this.isResizing = false;
      this.pendingResize = false;
      console.error("❌ Ошибка при обновлении размера Bitrix iframe:", error);
    }
  }

  forceResize() {
    this.lastHeight = 0;
    this.isResizing = false;
    this.pendingResize = false;
    this.scheduleResize(0);
  }

  destroy() {
    this.isInitialized = false;
    this.isResizing = false;
    this.pendingResize = false;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.isPageScrollable = true;
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }
}

const bitrixResize = new BitrixResize();

export default bitrixResize;
